import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, Mail, Phone, User } from "lucide-react";
import { buildTrackingPayload, postTrackingEvent } from "@/lib/tracking";
import { trackLead } from "@/lib/ga4";
import { trackLeadConversion } from "@/lib/conversions";

/**
 * Custom field IDs (registered in the CRM) for the BCBS Federal Depression
 * Info Session lead form.
 */
const FIELD_SEEKING_FOR = "z498cIjvX68ljnNXBw61";
const FIELD_COVERAGE_STATUS = "8Y9jhtTXC0KsyEvE7v7Z";
const FIELD_LEARN_ABOUT = "whCp8xaIJ6PIltrzySLu";

type Status = "idle" | "submitting" | "success";

const SEEKING_FOR_OPTIONS = [
  "Myself",
  "Spouse",
  "Child",
  "Parent",
  "Other family member",
];

const COVERAGE_OPTIONS = ["Yes", "I believe so", "Not sure", "No"];

const LEARN_OPTIONS = [
  "New depression treatment options",
  "Whether TMS may be appropriate",
  "Insurance benefits",
  "Treatment timeline",
  "Help for a family member",
  "I'm just learning",
];

const BcbsFederalInfoForm: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [seekingFor, setSeekingFor] = useState("");
  const [coverage, setCoverage] = useState("");
  const [learnAbout, setLearnAbout] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !phone.trim() ||
      !email.trim()
    ) {
      setError("Please fill in your first name, last name, phone, and email.");
      return;
    }

    const emailClean = email.trim().toLowerCase();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailClean);
    if (!emailValid) {
      setError("Please enter a valid email address.");
      return;
    }

    setStatus("submitting");

    try {
      const payload = buildTrackingPayload(
        "bcbs-federal-depression-info-session",
        "BCBS Federal Depression Info Session",
      );

      payload.formData.first_name = firstName.trim();
      payload.formData.last_name = lastName.trim();
      payload.formData.phone = phone.trim();
      payload.formData.email = emailClean;
      if (zip.trim()) payload.formData.postal_code = zip.trim();

      payload.formLabels.first_name = "First Name";
      payload.formLabels.last_name = "Last Name";
      payload.formLabels.phone = "Phone";
      payload.formLabels.email = "Email";
      payload.formLabels.postal_code = "ZIP Code";

      postTrackingEvent(payload, {
        customFields: {
          [FIELD_SEEKING_FOR]: {
            value: seekingFor,
            label: "Seeking Info For",
          },
          [FIELD_COVERAGE_STATUS]: {
            value: coverage,
            label: "BCBS Federal Coverage Status",
          },
          [FIELD_LEARN_ABOUT]: {
            value: learnAbout,
            label: "Most Like to Learn About",
          },
        },
      });

      await new Promise((r) => setTimeout(r, 600));
      trackLead(
        "bcbs-federal-depression-info-session",
        "BCBS Federal Depression Info Session",
      );
      trackLeadConversion(
        "bcbs-federal-depression-info-session",
        "BCBS Federal Depression Info Session",
      );
      setStatus("success");
    } catch {
      setStatus("idle");
      setError(
        "Something went wrong. Please try again or call us at 954-827-9793.",
      );
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 shadow-soft text-center space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/15 text-accent">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h3 className="font-headings text-xl font-bold text-primary">
          We Received Your Request
        </h3>
        <p className="text-foreground/80 text-base leading-relaxed">
          Thank you for your interest in the BCBS Federal Depression Information
          Session. A Neuro Wellness team member will send you the session
          details shortly.
        </p>
        <p className="text-sm text-muted-foreground">
          Prefer to talk now? Call{" "}
          <a
            href="tel:9548279793"
            className="font-bold text-accent hover:underline"
          >
            954-827-9793
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft space-y-5">
      <div className="space-y-1.5">
        <h3 className="font-headings text-xl sm:text-2xl font-bold text-primary">
          Get the Info Session Details
        </h3>
        <p className="text-sm text-muted-foreground">
          Fill out the short form and a Neuro Wellness team member will send you
          the information-session details.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label
              htmlFor="bcbs-fed-first"
              className="text-sm font-semibold text-primary"
            >
              First Name
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="bcbs-fed-first"
                type="text"
                autoComplete="given-name"
                placeholder="Jane"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="pl-9"
                disabled={status === "submitting"}
                required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label
              htmlFor="bcbs-fed-last"
              className="text-sm font-semibold text-primary"
            >
              Last Name
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="bcbs-fed-last"
                type="text"
                autoComplete="family-name"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="pl-9"
                disabled={status === "submitting"}
                required
              />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label
              htmlFor="bcbs-fed-phone"
              className="text-sm font-semibold text-primary"
            >
              Phone
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="bcbs-fed-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(954) 555-0123"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="pl-9"
                disabled={status === "submitting"}
                required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label
              htmlFor="bcbs-fed-zip"
              className="text-sm font-semibold text-primary"
            >
              ZIP Code
            </Label>
            <Input
              id="bcbs-fed-zip"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="33065"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              disabled={status === "submitting"}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="bcbs-fed-email"
            className="text-sm font-semibold text-primary"
          >
            Email Address
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="bcbs-fed-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-9"
              disabled={status === "submitting"}
              required
            />
          </div>
        </div>

        {/* Question 1 */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-primary mb-2">
            Who are you looking for information for?
          </legend>
          <div className="flex flex-wrap gap-2">
            {SEEKING_FOR_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setSeekingFor(opt)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  seekingFor === opt
                    ? "border-accent bg-accent text-white"
                    : "border-border bg-background text-foreground/80 hover:border-accent/50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Question 2 */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-primary mb-2">
            Do you have Blue Cross Blue Shield Federal Employee Program
            coverage?
          </legend>
          <div className="flex flex-wrap gap-2">
            {COVERAGE_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setCoverage(opt)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  coverage === opt
                    ? "border-accent bg-accent text-white"
                    : "border-border bg-background text-foreground/80 hover:border-accent/50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Question 3 */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-primary mb-2">
            What would you most like to learn about?
          </legend>
          <div className="flex flex-wrap gap-2">
            {LEARN_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setLearnAbout(opt)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  learnAbout === opt
                    ? "border-accent bg-accent text-white"
                    : "border-border bg-background text-foreground/80 hover:border-accent/50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </fieldset>

        {error && (
          <p className="text-sm text-destructive font-medium" role="alert">
            {error}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          disabled={status === "submitting"}
          className="w-full bg-[#f59e0b] hover:bg-[#d97706] text-slate-950 font-black tracking-wide text-base sm:text-lg uppercase py-6 rounded-xl shadow-lg transition-all transform active:scale-95"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Checking
              Benefits…
            </>
          ) : (
            "Check Your Benefits ›"
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center leading-relaxed">
          By submitting this form, you are requesting information from Neuro
          Wellness TMS Centers of America. Submission does not guarantee
          insurance coverage, authorization, eligibility, or treatment. Benefits
          vary by plan and individual circumstances.
        </p>
      </form>
    </div>
  );
};

export default BcbsFederalInfoForm;
